/**
 * Single source of truth for everything a search engine or social platform
 * reads from the document head: page titles, descriptions, canonical URLs and
 * structured data.
 *
 * Used twice:
 *  - at runtime by SeoManager, which keeps the head in sync while navigating;
 *  - at build time by scripts/prerender.mjs, which bakes the same values into
 *    the static HTML of every route so "view source" shows real SEO markup.
 */

export const SITE_NAME = "FX Order Types";
/** Production origin. Canonical URLs must always point at the live domain,
 *  never at localhost or a staging host. */
export const SITE_URL = "https://fxordertypes.com";

export const OG_IMAGE =
  "https://readdy.ai/api/search-image?query=Minimal%20editorial%20cover%20for%20a%20forex%20education%20site%2C%20near-black%20background%20with%20a%20single%20bright%20teal%20ascending%20price%20line%20crossing%20a%20white%20horizontal%20axis%2C%20two%20faint%20horizontal%20grid%20lines%2C%20bold%20high%20contrast%20geometric%20composition%2C%20wide%20empty%20space%20on%20the%20left%2C%20clean%20flat%20vector%20aesthetic%2C%20no%20text&width=1200&height=630&seq=pipecraft-og-cover-01&orientation=landscape";

export interface PageMeta {
  title: string;
  description: string;
}

export const DEFAULT_META: PageMeta = {
  title: "Forex Order Types: Market, Limit, Stop & Stop-Limit Orders",
  description:
    "Explore the main forex order types — market, limit, stop and stop-limit orders. Learn how each one executes, when to use it, and how to manage slippage and risk.",
};

export const PAGE_META: Record<string, PageMeta> = {
  "/": DEFAULT_META,
  "/market-orders": {
    title: "Market Order Explained: Fill Now, Price Not Guaranteed",
    description:
      "A market order fills immediately at the best available price — but not the price you clicked. See how the fill is made, what it costs and when to avoid it.",
  },
  "/limit-orders": {
    title: "Limit Order Explained: Your Price or No Fill at All",
    description:
      "A limit order only fills at your price or better, so you keep full price control and carry fill risk instead. Worked example, costs and when to use it.",
  },
  "/stop-orders": {
    title: "Stop Order & Stop-Loss Explained: Triggers That Protect",
    description:
      "A stop order waits for your trigger price, then becomes a market order. How stop-losses cap risk, how breakout entries work, and why fills can slip.",
  },
  "/stop-limit-orders": {
    title: "Stop-Limit Order Explained: Trigger, Limit and Fill Risk",
    description:
      "A stop-limit order caps the worst price you accept but may never fill. See the stop-to-limit band, its risks, and how to choose between it and a stop.",
  },
  "/order-execution": {
    title: "Order Execution & Slippage: What Happens After You Click",
    description:
      "Follow a forex order from click to fill: routing, latency, spread, commission and slippage. See what each cost really adds to a single round trip.",
  },
  "/glossary": {
    title: "Forex Glossary: 32 Order, Cost, Execution and Risk Terms",
    description:
      "Searchable forex glossary covering order types, spreads and commissions, execution and slippage, leverage and margin — each term defined in a sentence.",
  },
};

/** Every indexable route, prerendered to static HTML at build time. */
export const PRERENDER_PATHS = [
  "/",
  "/market-orders",
  "/limit-orders",
  "/stop-orders",
  "/stop-limit-orders",
  "/order-execution",
  "/glossary",
];

export interface PageSeo {
  /** Path with trailing slash stripped; "/" stays "/". */
  normalized: string;
  isKnownPage: boolean;
  /** Full document title as it should appear in <title> and og:title. */
  title: string;
  description: string;
  /** Absolute production URL for canonical / og:url. */
  canonical: string;
  /** Robots directive — unknown routes must never be indexed. */
  robots: string;
}

export function normalizePath(pathname: string): string {
  return pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
}

export function getPageSeo(pathname: string): PageSeo {
  const normalized = normalizePath(pathname);
  const meta = PAGE_META[normalized];
  const isKnownPage = Boolean(meta);
  const page = meta ?? DEFAULT_META;
  return {
    normalized,
    isKnownPage,
    title: isKnownPage ? page.title : `Page not found (404) | ${SITE_NAME}`,
    description: page.description,
    canonical: `${SITE_URL}${normalized === "/" ? "/" : normalized}`,
    robots: isKnownPage ? "index, follow" : "noindex, follow",
  };
}

/** JSON-LD structured data shared by every page (WebSite + Organization),
 *  with a WebPage node describing the current route. */
export function buildJsonLd(seo: PageSeo) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: SITE_NAME,
        description:
          "An educational reference for forex order types — how market, limit, stop and stop-limit orders execute, what they cost and when to use each one.",
        inLanguage: "en",
        publisher: { "@id": `${SITE_URL}/#publisher` },
      },
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#publisher`,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        description:
          "Educational content on forex order types, order execution, trading costs and risk management.",
      },
      {
        "@type": "WebPage",
        "@id": `${seo.canonical}#webpage`,
        url: seo.canonical,
        name: seo.title,
        description: seo.description,
        inLanguage: "en",
        isPartOf: { "@id": `${SITE_URL}/#website` },
      },
    ],
  };
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * The complete <head> block for one page as static markup, inserted at build
 * time by scripts/prerender.mjs. Runtime navigation keeps the same tags in
 * sync through SeoManager, which reads the exact same data from above.
 */
export function buildHeadHtml(seo: PageSeo): string {
  const jsonLd = JSON.stringify(buildJsonLd(seo)).replace(/</g, "\\u003c");
  const canonicalTags = seo.isKnownPage
    ? [
        `<link rel="canonical" href="${escapeAttr(seo.canonical)}" />`,
        `<meta property="og:url" content="${escapeAttr(seo.canonical)}" />`,
      ]
    : [];
  const tags = [
    `<title>${escapeAttr(seo.title)}</title>`,
    `<meta name="description" content="${escapeAttr(seo.description)}" />`,
    `<meta name="robots" content="${seo.robots}" />`,
    ...canonicalTags,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${escapeAttr(SITE_NAME)}" />`,
    `<meta property="og:locale" content="en" />`,
    `<meta property="og:title" content="${escapeAttr(seo.title)}" />`,
    `<meta property="og:description" content="${escapeAttr(seo.description)}" />`,
    `<meta property="og:image" content="${escapeAttr(OG_IMAGE)}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="${escapeAttr(`${SITE_NAME} — a visual guide to forex order types`)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeAttr(seo.title)}" />`,
    `<meta name="twitter:description" content="${escapeAttr(seo.description)}" />`,
    `<meta name="twitter:image" content="${escapeAttr(OG_IMAGE)}" />`,
    `<script type="application/ld+json">${jsonLd}</script>`,
  ];
  return tags.join("\n    ");
}